import { memo, useCallback } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { ListItemIcon, MenuItem, Typography, Divider, Skeleton } from '@mui/material'
import { AvcInstanceTask } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useAvcInstanceTaskDeleteMutation } from 'hooks/mutations/content'
import { avcInstanceTasksRoute } from 'pages/NWPlatform/AvcSystem/Instances/Instance'
import { avcInstanceTaskQueryKeys } from 'services/queryKeys'
import { AvcInstanceTaskSelectedFields } from 'types/AvcInstanceTaskSelectedFields'

type ExtendedRowActionProps = RowActionsProps<AvcInstanceTaskSelectedFields> & {
  onDuplicate: (avcInstanceTask: AvcInstanceTask) => void
}

const RowActionsRaw = ({ row, onClose, onDuplicate }: ExtendedRowActionProps) => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { avcInstanceId: instanceId } = avcInstanceTasksRoute.useParams()
  const { query } = avcInstanceTasksRoute.useSearch()

  const avcInstanceTaskQuery = useQuery(avcInstanceTaskQueryKeys.detail({ instanceId, taskId: row.original.id }))

  const { mutateAsync: deleteTask } = useAvcInstanceTaskDeleteMutation()

  const handleDuplicateTask = useCallback(() => {
    if (avcInstanceTaskQuery.data) {
      onClose()

      setTimeout(() => {
        onDuplicate(avcInstanceTaskQuery.data)
      }, 300)
    }
  }, [avcInstanceTaskQuery.data, onClose, onDuplicate])

  const handleDeleteTask = useCallback(async () => {
    onClose()

    deleteTask(
      { instanceId, taskId: row.original.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:ContentProcessing.Instance.nameDelete', { name: row.original.name }), {
            variant: 'success',
          })
          return queryClient.invalidateQueries(avcInstanceTaskQueryKeys.list({ view: 'FULL', instanceId, query }))
        },
      },
    )
  }, [deleteTask, enqueueSnackbar, instanceId, onClose, query, queryClient, row.original.id, row.original.name, t])

  return (
    <>
      <MenuItem onClick={handleDuplicateTask} disabled={!avcInstanceTaskQuery.data}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {avcInstanceTaskQuery.isLoading ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemDelete onConfirm={handleDeleteTask} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
